import isSameDay from 'date-fns/isSameDay';
import parseISO from 'date-fns/parseISO';
import ICAL from 'ical.js';
import formatDate from 'utils/formatDate';
import req from 'utils/req';

import { FULL_DAY_THRESHOLD_IN_MINUTES } from '../utils/constants';

function parseEvent(icalEvent: string) {
  const icalString = `BEGIN:VCALENDAR\n${icalEvent}\nEND:VCALENDAR`;
  const jcalData = ICAL.parse(icalString);
  const comp = new ICAL.Component(jcalData);
  const vevent = comp.getFirstSubcomponent('vevent');
  const event = new ICAL.Event(vevent);
  return event;
}

export type AgendaEvent = {
  id: string;
  ical: string;
  tripId: string;
  experienceId?: string;
  event: ICAL.Event;
  created_at: string;
  last_modified_at: string;
  deleted_at: string;
};

export async function getEventList({ from, to, experienceId }) {
  const searchParams = new URLSearchParams();
  searchParams.set('from', from);
  searchParams.set('to', to);
  if (experienceId) {
    searchParams.set('experienceId', experienceId);
  }
  const response: { data: Omit<AgendaEvent, 'event'>[] } = await req(
    `${URL_API_HOST}/api/v3/agenda?${searchParams.toString()}`,
  ).get();
  if (!response?.data) {
    return [];
  }

  return response.data.map((event) => {
    const parsedEvent = parseEvent(event.ical);
    const agendaEvent: AgendaEvent = {
      ...event,
      event: parsedEvent,
    };
    return agendaEvent;
  });
}

export async function getAgendaEvent({ eventId }): Promise<any> {
  const response = await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).get();
  if (!response?.data) {
    return {};
  }
  const parsedEvent = parseEvent(response.data.ical);
  const startDate = parseISO(
    parsedEvent.startDate.toICALString().replace('Z', ''),
  );
  const endDate = parseISO(parsedEvent.endDate.toICALString().replace('Z', ''));

  const eventDuration = Math.floor(parsedEvent.duration.toSeconds() / 60);

  const isMultiDay = !isSameDay(startDate, endDate);
  const isFullDay =
    !isMultiDay && eventDuration >= FULL_DAY_THRESHOLD_IN_MINUTES;
  const recurrence = parsedEvent.component.getFirstPropertyValue(
    'rrule',
  ) as unknown as { freq: string; until?: any };
  const summary = parsedEvent.component.getFirstPropertyValue('summary');

  return {
    ...response.data,
    event: parsedEvent,
    summary,
    isFullDay,
    isMultiDay,
    start: formatDate(startDate, 'yyyy-MM-dd'),
    end: formatDate(endDate, 'yyyy-MM-dd'),
    startTime: formatDate(startDate, 'HH:mm'),
    endTime: formatDate(endDate, 'HH:mm'),
    eventRecurrence: recurrence?.freq || '',
    createdAt: response.data.created_at + 'Z',
    lastModified: response.data.last_modified_at
      ? response.data.last_modified_at + 'Z'
      : null,
    eventStart: startDate,
    experienceId: response.data.experienceId,
  };
}

export async function createEvent(body: {
  experienceId: string;
  ical: string;
}): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda`).post(body);
}

export async function updateEvent({ eventId, body }): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).put({
    ...body,
    type: 'update',
  });
}

export async function updateRecurring({ eventId, body }): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).put({
    ...body,
    type: 'update_recurring',
  });
}

export async function addException({ eventId, exception }): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).put({
    exception,
    type: 'add_exception',
  });
}

export async function stopRecurrence({ eventId, from }): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).put({
    from,
    type: 'stop_recurrence',
  });
}

export async function deleteEvent({ eventId }): Promise<any> {
  return await req(`${URL_API_HOST}/api/v3/agenda/${eventId}`).delete();
}
